import { formatLocalizedDate, t } from './i18n.mjs?v=20260806.100';
import {
  customerDate,
  groupCustomerContainerViews,
  mergeCustomerPages,
  sanitizeCustomerContainerView,
} from './customer-dashboard-utils.mjs?v=20260806.100';

const MODES = Object.freeze(['operational', 'due', 'completed']);

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function genericError() {
  return new Error('Customer dashboard is unavailable.');
}

export function createCustomerDashboardSource({ uid, db, callList, firestore, loadEvidence = null }) {
  if (typeof uid !== 'string' || !uid || typeof callList !== 'function'
    || typeof firestore?.doc !== 'function' || typeof firestore?.getDoc !== 'function') {
    throw new TypeError('A signed-in Customer dashboard source is required.');
  }
  const source = {
    async listPage(mode, cursor = null) {
      if (!MODES.includes(mode) || (cursor !== null && (typeof cursor !== 'string' || !cursor))) throw genericError();
      try {
        const payload = cursor ? { mode, cursor } : { mode };
        const response = await callList(payload);
        const data = response?.data;
        if (!data || !Array.isArray(data.items)) throw genericError();
        const items = data.items.map((item) => sanitizeCustomerContainerView(item, { uid }));
        return mergeCustomerPages([], { ...data, items });
      } catch (_) {
        throw genericError();
      }
    },
    async loadDetail(id) {
      if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(id)) {
        throw new TypeError('Customer container id is invalid.');
      }
      try {
        const reference = firestore.doc(db, 'customer_container_views', id);
        const snapshot = await firestore.getDoc(reference);
        if (!snapshot?.exists?.() || snapshot.id !== id) throw genericError();
        return sanitizeCustomerContainerView({ id, ...snapshot.data() }, { uid });
      } catch (_) {
        throw genericError();
      }
    },
  };
  if (typeof firestore?.onSnapshot === 'function') {
    source.subscribeChanges = (onChange, onError = () => {}) => {
      if (typeof onChange !== 'function') throw new TypeError('A realtime Customer listener is required.');
      let queued = false;
      const signal = () => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => { queued = false; onChange(); });
      };
      const signalRef = firestore.doc(db, 'customer_dashboard_signals', uid);
      const stop = firestore.onSnapshot(signalRef, signal, onError);
      return () => stop?.();
    };
  }
  if (typeof loadEvidence === 'function') source.loadEvidence = loadEvidence;
  return Object.freeze(source);
}

function loadingMarkup(locale, detail = false) {
  return `
    <section class="customer-dashboard customer-dashboard-skeleton" data-review-id="${detail ? 'customer.detail' : 'customer.dashboard'}" aria-busy="true" aria-label="${escapeHtml(t(detail ? 'customerDashboard.detail.loading' : 'customerDashboard.loading', {}, locale))}">
      <div class="customer-dashboard-skeleton-title"></div>
      <div class="customer-dashboard-skeleton-grid"><div></div><div></div><div></div></div>
    </section>`;
}

function display(value, locale) {
  return value === null || value === undefined || value === ''
    ? t('customerDashboard.notProvided', {}, locale) : String(value);
}

function displayDate(value, locale) {
  const text = formatLocalizedDate(customerDate(value), locale);
  return text && !text.startsWith('Dec 31, 9999') && !text.includes('9999')
    ? text : t('customerDashboard.notProvided', {}, locale);
}

function statusText(value, locale) {
  const key = `customerDashboard.status.${value}`;
  const localized = t(key, {}, locale);
  return localized === key ? String(value || '').replaceAll('_', ' ') : localized;
}

function fieldsForCard(item, locale) {
  return [
    ['customerDashboard.field.dcContainer', display(item.dc_container_no, locale)],
    ['customerDashboard.field.job', display(item.job_no, locale)],
    ['customerDashboard.field.terminal', display(item.terminal_name, locale)],
    ['customerDashboard.field.ssl', display(item.ssl, locale)],
    ['customerDashboard.field.sizeType', display(item.size_type, locale)],
    ['customerDashboard.field.stage', statusText(item.current_stage, locale)],
    ['customerDashboard.field.eta', displayDate(item.eta_at, locale)],
    ['customerDashboard.field.lfd', displayDate(item.lfd_at, locale)],
    ['customerDashboard.field.due', displayDate(item.due_at, locale)],
    ['customerDashboard.field.pickupAppointment', displayDate(item.pickup_appointment_at, locale)],
    ['customerDashboard.field.deliveryAppointment', displayDate(item.delivery_appointment_at, locale)],
  ];
}

function cardMarkup(item, groupKey, locale) {
  const fields = fieldsForCard(item, locale);
  return `
    <article class="customer-dashboard-card customer-dashboard-priority-${escapeHtml(groupKey)}" data-customer-container-id="${escapeHtml(item.id)}">
      <header class="customer-dashboard-card-heading">
        <div><h3>${escapeHtml(display(item.container_no, locale))}</h3><p>${escapeHtml(display(item.job_no, locale))}</p></div>
        <span class="customer-dashboard-status"><span aria-hidden="true">${groupKey === 'attention' ? '!' : groupKey === 'completed_recent' ? '&#10003;' : '&#9679;'}</span>${escapeHtml(statusText(item.status, locale))}</span>
      </header>
      <dl class="customer-dashboard-fields">
        ${fields.map(([key, value]) => `<div><dt>${escapeHtml(t(key, {}, locale))}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}
      </dl>
      <a class="customer-dashboard-detail-action" href="#/container/${encodeURIComponent(item.id)}" data-customer-detail-id="${escapeHtml(item.id)}">${escapeHtml(t('customerDashboard.viewDetail', {}, locale))}<span aria-hidden="true">&#8250;</span></a>
    </article>`;
}

function groupMarkup(group, locale) {
  return `
    <section class="customer-dashboard-group" data-customer-group="${escapeHtml(group.key)}" aria-labelledby="customer-dashboard-${escapeHtml(group.key)}">
      <header class="customer-dashboard-group-heading"><h2 id="customer-dashboard-${escapeHtml(group.key)}">${escapeHtml(t(`customerDashboard.group.${group.key}`, {}, locale))}</h2><span>${escapeHtml(t('customerDashboard.count', { count: group.items.length }, locale))}</span></header>
      ${group.items.length
        ? `<div class="customer-dashboard-card-grid">${group.items.map((item) => cardMarkup(item, group.key, locale)).join('')}</div>`
        : `<p class="customer-dashboard-group-empty">${escapeHtml(t('customerDashboard.groupEmpty', {}, locale))}</p>`}
    </section>`;
}

export function mountCustomerDashboard({ container, source, user, locale = 'en', now = null, navigate = (hash) => { globalThis.location.hash = hash; } }) {
  if (!container || typeof source?.listPage !== 'function' || user?.role !== 'customer'
    || typeof user.uid !== 'string' || !user.uid
    || (now !== null && typeof now !== 'function' && !(now instanceof Date))
    || (now instanceof Date && !Number.isFinite(now.getTime()))) {
    throw new TypeError('A signed-in Customer dashboard is required.');
  }
  const readClock = () => {
    const value = typeof now === 'function' ? now() : now instanceof Date ? now : new Date();
    if (!(value instanceof Date) || !Number.isFinite(value.getTime())) throw new TypeError('Current time is unavailable.');
    return new Date(value.getTime());
  };
  const state = {
    locale, loading: true, error: false, refreshWarning: false, destroyed: false, generation: 0,
    classificationNow: null,
    pages: Object.fromEntries(MODES.map((mode) => [mode, { items: [], nextCursor: null, maybeMore: false, cursorUnavailable: false, pending: false }])),
  };
  const allItems = () => MODES.flatMap((mode) => state.pages[mode].items);

  function render() {
    if (state.destroyed) return;
    if (state.loading) { container.innerHTML = loadingMarkup(state.locale); return; }
    if (state.error) {
      container.innerHTML = `<section class="customer-dashboard customer-dashboard-error" data-review-id="customer.dashboard" role="alert"><h1>${escapeHtml(t('customerDashboard.title', {}, state.locale))}</h1><p>${escapeHtml(t('customerDashboard.error', {}, state.locale))}</p><button type="button" data-customer-action="retry">${escapeHtml(t('customerDashboard.retry', {}, state.locale))}</button></section>`;
      return;
    }
    const groups = groupCustomerContainerViews(allItems(), { now: state.classificationNow });
    const total = groups.reduce((sum, group) => sum + group.items.length, 0);
    const unavailableMore = MODES.some((mode) => state.pages[mode].maybeMore && state.pages[mode].cursorUnavailable);
    container.innerHTML = `
      <section class="customer-dashboard" data-review-id="customer.dashboard">
        <header class="customer-dashboard-heading"><div><h1>${escapeHtml(t('customerDashboard.title', {}, state.locale))}</h1><p>${escapeHtml(t('customerDashboard.subtitle', {}, state.locale))}</p></div><button type="button" data-customer-action="refresh">${escapeHtml(t('customerDashboard.refresh', {}, state.locale))}</button></header>
        <p class="customer-dashboard-consistency" role="note">${escapeHtml(t('customerDashboard.consistency', {}, state.locale))}</p>
        ${state.refreshWarning ? `<p class="customer-dashboard-warning" role="alert">${escapeHtml(t('customerDashboard.refreshWarning', {}, state.locale))}</p>` : ''}
        ${unavailableMore ? `<p class="customer-dashboard-warning" role="status">${escapeHtml(t('customerDashboard.moreUnavailable', {}, state.locale))}</p>` : ''}
        ${total === 0 ? `<p class="customer-dashboard-empty">${escapeHtml(t('customerDashboard.empty', {}, state.locale))}</p>` : ''}
        <div class="customer-dashboard-groups">${groups.map((group) => groupMarkup(group, state.locale)).join('')}</div>
        <div class="customer-dashboard-pagination" aria-label="${escapeHtml(t('customerDashboard.pagination', {}, state.locale))}">
          ${MODES.map((mode) => state.pages[mode].maybeMore && !state.pages[mode].cursorUnavailable
            ? `<button type="button" data-customer-more="${mode}" ${state.pages[mode].pending ? 'disabled' : ''}>${escapeHtml(t(`customerDashboard.more.${mode}`, {}, state.locale))}</button>` : '').join('')}
        </div>
      </section>`;
  }

  async function reload({ preserve = false } = {}) {
    const generation = ++state.generation;
    if (!preserve) { state.loading = true; state.error = false; render(); }
    try {
      const classificationNow = readClock();
      const results = await Promise.all(MODES.map((mode) => source.listPage(mode)));
      if (state.destroyed || generation !== state.generation) return;
      MODES.forEach((mode, index) => { state.pages[mode] = { ...results[index], pending: false }; });
      state.classificationNow = classificationNow;
      state.loading = false; state.error = false; state.refreshWarning = false; render();
    } catch (_) {
      if (state.destroyed || generation !== state.generation) return;
      state.loading = false;
      MODES.forEach((mode) => { state.pages[mode] = { ...state.pages[mode], pending: false }; });
      if (preserve && allItems().length) state.refreshWarning = true;
      else state.error = true;
      render();
    }
  }

  async function loadMore(mode) {
    const page = state.pages[mode];
    if (!page?.maybeMore || page.cursorUnavailable || !page.nextCursor || page.pending) return;
    const generation = state.generation;
    page.pending = true; render();
    try {
      const next = await source.listPage(mode, page.nextCursor);
      if (state.destroyed || generation !== state.generation || state.pages[mode] !== page) return;
      state.pages[mode] = { ...mergeCustomerPages(page.items, next), pending: false };
      render();
    } catch (_) {
      if (state.destroyed || generation !== state.generation || state.pages[mode] !== page) return;
      page.pending = false; state.refreshWarning = true; render();
    }
  }

  function onClick(event) {
    const detail = event.target.closest?.('[data-customer-detail-id]');
    if (detail) { event.preventDefault(); navigate(`#/container/${encodeURIComponent(detail.dataset.customerDetailId)}`); return; }
    const mode = event.target.closest?.('[data-customer-more]')?.dataset.customerMore;
    if (MODES.includes(mode)) { void loadMore(mode); return; }
    const action = event.target.closest?.('[data-customer-action]')?.dataset.customerAction;
    if (action === 'retry') void reload();
    if (action === 'refresh') void reload({ preserve: true });
  }
  container.addEventListener('click', onClick);
  void reload();
  const stopChanges = typeof source.subscribeChanges === 'function'
    ? source.subscribeChanges(
      () => { if (!state.destroyed) void reload({ preserve: true }); },
      () => {
        if (state.destroyed) return;
        state.refreshWarning = true;
        render();
      },
    )
    : null;
  return Object.freeze({
    setLocale(next) { state.locale = next === 'zh-CN' ? 'zh-CN' : 'en'; render(); },
    reload: () => reload({ preserve: true }),
    destroy() { state.destroyed = true; state.generation += 1; stopChanges?.(); container.removeEventListener('click', onClick); },
  });
}

function detailFields(item, locale) {
  return [
    ['customerDashboard.field.status', statusText(item.status, locale)],
    ['customerDashboard.field.stage', statusText(item.current_stage, locale)],
    ...fieldsForCard(item, locale).filter(([key]) => key !== 'customerDashboard.field.stage'),
    ['customerDashboard.field.loadOut', displayDate(item.load_out_at, locale)],
    ['customerDashboard.field.loadedDelivered', displayDate(item.loaded_delivered_at, locale)],
    ['customerDashboard.field.emptyReady', displayDate(item.empty_ready_at, locale)],
    ['customerDashboard.field.emptyPickedUp', displayDate(item.empty_picked_up_at, locale)],
    ['customerDashboard.field.emptyReturned', displayDate(item.empty_returned_at, locale)],
  ];
}

export function mountCustomerContainerDetail({ container, source, id, locale = 'en', back = () => history.back() }) {
  if (!container || typeof source?.loadDetail !== 'function') throw new TypeError('Customer detail source is required.');
  const state = {
    locale, item: null, evidence: [], evidenceCleanup: null,
    loading: true, error: false, destroyed: false, generation: 0,
  };
  function render() {
    if (state.destroyed) return;
    if (state.loading) { container.innerHTML = loadingMarkup(state.locale, true); return; }
    if (state.error) { container.innerHTML = `<section class="customer-dashboard customer-dashboard-error" role="alert"><h1>${escapeHtml(t('customerDashboard.detail.title', {}, state.locale))}</h1><p>${escapeHtml(t('customerDashboard.detail.error', {}, state.locale))}</p><button type="button" data-customer-detail-action="retry">${escapeHtml(t('customerDashboard.retry', {}, state.locale))}</button></section>`; return; }
    container.innerHTML = `<article class="customer-dashboard customer-dashboard-detail"><header class="customer-dashboard-heading"><div><p>${escapeHtml(t('customerDashboard.detail.eyebrow', {}, state.locale))}</p><h1>${escapeHtml(display(state.item.container_no, state.locale))}</h1></div><button type="button" data-customer-detail-action="back">${escapeHtml(t('common.back', {}, state.locale))}</button></header><dl class="customer-dashboard-detail-fields">${detailFields(state.item, state.locale).map(([key, value]) => `<div><dt>${escapeHtml(t(key, {}, state.locale))}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}</dl>${state.evidence.length ? `<section class="customer-dashboard-evidence"><h2>POD &amp; Empty Return evidence</h2><div class="workflow-evidence-grid">${state.evidence.map((item) => `<figure class="workflow-evidence-card">${item.objectUrl ? `<img src="${escapeHtml(item.objectUrl)}" alt="${escapeHtml(item.kind === 'empty_return' ? 'Empty Return evidence' : 'POD evidence')}">` : '<div class="workflow-evidence-unavailable">Preview unavailable</div>'}<figcaption><strong>${escapeHtml(item.kind === 'empty_return' ? 'Empty Return' : 'POD')}</strong><span>${escapeHtml(item.note || 'No note')}</span><time>${escapeHtml(displayDate(item.created_at, state.locale))}</time></figcaption></figure>`).join('')}</div></section>` : ''}<p class="customer-dashboard-consistency">${escapeHtml(t('customerDashboard.completionRule', {}, state.locale))}</p><p class="customer-dashboard-consistency">${escapeHtml(t('customerDashboard.consistency', {}, state.locale))}</p></article>`;
  }
  async function load() {
    const generation = ++state.generation; state.loading = true; state.error = false; render();
    try {
      const [item, evidenceResult] = await Promise.all([
        source.loadDetail(id),
        typeof source.loadEvidence === 'function'
          ? source.loadEvidence(id).catch(() => ({ items: [], cleanup: null }))
          : Promise.resolve({ items: [], cleanup: null }),
      ]);
      if (!state.destroyed && generation === state.generation) {
        state.evidenceCleanup?.();
        state.item = item;
        state.evidence = Array.isArray(evidenceResult?.items) ? evidenceResult.items : [];
        state.evidenceCleanup = typeof evidenceResult?.cleanup === 'function' ? evidenceResult.cleanup : null;
        state.loading = false;
        render();
      } else {
        evidenceResult?.cleanup?.();
      }
    }
    catch (_) { if (!state.destroyed && generation === state.generation) { state.loading = false; state.error = true; render(); } }
  }
  function onClick(event) { const action = event.target.closest?.('[data-customer-detail-action]')?.dataset.customerDetailAction; if (action === 'retry') void load(); if (action === 'back') back(); }
  container.addEventListener('click', onClick); void load();
  return Object.freeze({ setLocale(next) { state.locale = next === 'zh-CN' ? 'zh-CN' : 'en'; render(); }, destroy() { state.destroyed = true; state.generation += 1; state.evidenceCleanup?.(); container.removeEventListener('click', onClick); } });
}
